/**
 * Schedule Detail
 *
 * Slide-out panel showing full schedule details with tabbed interface:
 *  - Overview: status, triggers, settings, enable/disable, run now / stop
 *  - Prompt: editor with version history and revert
 *  - Logs: scrollable log viewer with type filtering
 *  - Runs: run history table with trigger type, cost, duration
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  Play,
  Pause,
  Square,
  Clock,
  Zap,
  GitBranch,
  Loader2,
  RotateCcw,
  Save,
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
  ExternalLink,
  Copy,
  Check,
  Pencil,
} from 'lucide-react';
import {
  AgentScheduleService,
  type Schedule,
  type RunEntry,
  type LogEntry,
  type PromptVersion,
  type PendingEvent,
  type LogType,
  type UpdateScheduleData,
} from '../services/agentScheduleService';
import RunHistoryOrbs from './RunHistoryOrbs';

type DetailTab = 'overview' | 'prompt' | 'logs' | 'events' | 'runs';

interface ScheduleDetailProps {
  schedule: Schedule;
  runs: RunEntry[];
  token: string;
  onClose: () => void;
  onRefresh: () => void;
  onRunNow: (id: string) => void;
  onStop: (id: string) => void;
  onToggleEnabled: (id: string, currentlyEnabled: boolean) => void;
}

// --- Helpers ---

function cronToHuman(expr: string | null): string {
  if (!expr) return 'None';
  const parts = expr.trim().split(/\s+/);
  if (parts.length < 5) return expr;
  const [min, hour, dom, mon, dow] = parts;
  if (min === '*' && hour === '*') return 'Every minute';
  if (hour === '*' && min !== '*') return `Every hour at :${min.padStart(2, '0')}`;
  if (dom === '*' && mon === '*' && dow === '*' && min !== '*' && hour !== '*') {
    return `Daily at ${hour.padStart(2, '0')}:${min.padStart(2, '0')} UTC`;
  }
  if (hour.includes('/')) {
    return `Every ${hour.split('/')[1]} hours`;
  }
  return expr;
}

/** Parse a date string that may be SQLite format (space-separated, no T/Z) */
function parseSqliteDate(dateStr: string): Date {
  // SQLite dates look like "2026-03-18 07:45:35" — add T and Z for reliable parsing
  let normalized = dateStr;
  if (normalized.includes(' ') && !normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T') + 'Z';
  }
  return new Date(normalized);
}

function timeUntilFromDate(date: Date): string {
  const diff = date.getTime() - Date.now();
  if (diff < 0) return 'overdue';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remMins = minutes % 60;
  if (hours < 24) {
    const now = new Date();
    const isNextDay = date.getUTCDate() !== now.getUTCDate();
    const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
    if (isNextDay && hours < 48) return `tomorrow at ${timeStr} UTC`;
    if (remMins > 0) return `in ${hours}h ${remMins}m`;
    return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  }
  const days = Math.floor(hours / 24);
  return `in ${days} day${days === 1 ? '' : 's'}`;
}

function formatNextRunTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const date = parseSqliteDate(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const isToday = date.getUTCDate() === now.getUTCDate() && date.getUTCMonth() === now.getUTCMonth() && date.getUTCFullYear() === now.getUTCFullYear();
    const tomorrow = new Date(now); tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    const isTomorrow = date.getUTCDate() === tomorrow.getUTCDate() && date.getUTCMonth() === tomorrow.getUTCMonth() && date.getUTCFullYear() === tomorrow.getUTCFullYear();
    const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
    if (isToday) return `Today at ${timeStr} UTC`;
    if (isTomorrow) {
      const dayStr = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
      return `Tomorrow, ${dayStr} at ${timeStr} UTC`;
    }
    const dayStr = date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    return `${dayStr} at ${timeStr} UTC`;
  } catch { return ''; }
}

/** Return a human-readable relative time string */
function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try {
    const date = parseSqliteDate(dateStr);
    if (isNaN(date.getTime())) return '--';
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return timeUntilFromDate(date);
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    if (hours < 48) return 'yesterday';
    const days = Math.floor(hours / 24);
    if (days < 14) return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    if (days < 60) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    // Fallback: formatted date
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '--';
  }
}

/** Return an exact datetime string suitable for a tooltip */
function exactDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const date = parseSqliteDate(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString();
  } catch {
    return '';
  }
}

function stringifyEventPayload(payload: unknown): string {
  if (payload == null) return '';
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed) return '';
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return payload;
    }
  }
  try {
    return JSON.stringify(payload, null, 2) ?? String(payload);
  } catch {
    return String(payload);
  }
}

function pendingEventPreview(payload: unknown): string {
  if (payload == null) return 'No payload';
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed) return 'Empty payload';
    return trimmed.length > 100 ? `${trimmed.slice(0, 97)}...` : trimmed;
  }
  if (Array.isArray(payload)) {
    return `${payload.length} item${payload.length === 1 ? '' : 's'}`;
  }
  if (typeof payload === 'object') {
    const keys = Object.keys(payload as Record<string, unknown>);
    if (keys.length === 0) return 'Empty object';
    const visibleKeys = keys.slice(0, 4).join(', ');
    return keys.length > 4 ? `${visibleKeys} +${keys.length - 4} more` : visibleKeys;
  }
  return String(payload);
}

/** Wrap a relative time in a span with the exact datetime as a tooltip */
function TimestampSpan({ dateStr, fallback = '--' }: { dateStr: string | null | undefined; fallback?: string }) {
  if (!dateStr) return <span>{fallback}</span>;
  const relative = timeAgo(dateStr);
  const exact = exactDateTime(dateStr);
  return <span title={exact || undefined}>{relative}</span>;
}

function logTypeIcon(type: LogType) {
  switch (type) {
    case 'error': return <XCircle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />;
    case 'warning': return <AlertTriangle className="h-3.5 w-3.5 text-yellow-400 flex-shrink-0" />;
    case 'run_start': return <Play className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" />;
    case 'run_end': return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />;
    case 'context': return <GitBranch className="h-3.5 w-3.5 text-purple-400 flex-shrink-0" />;
    default: return <Info className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />;
  }
}

function logTypeBadgeClass(type: LogType): string {
  switch (type) {
    case 'error': return 'bg-red-500/15 text-red-400';
    case 'warning': return 'bg-yellow-500/15 text-yellow-400';
    case 'run_start': return 'bg-blue-500/15 text-blue-400';
    case 'run_end': return 'bg-emerald-500/15 text-emerald-400';
    case 'context': return 'bg-purple-500/15 text-purple-400';
    default: return 'bg-gray-500/15 text-gray-400';
  }
}

function statusBadge(status: string | null): { label: string; className: string } {
  switch (status) {
    case 'success': return { label: 'Success', className: 'bg-emerald-500/20 text-emerald-400' };
    case 'failure': return { label: 'Failed', className: 'bg-red-500/20 text-red-400' };
    case 'timeout': return { label: 'Timeout', className: 'bg-orange-500/20 text-orange-400' };
    case 'running': return { label: 'Running', className: 'bg-blue-500/20 text-blue-400' };
    case 'pending': return { label: 'Pending', className: 'bg-gray-500/20 text-gray-400' };
    case 'skipped': return { label: 'Skipped', className: 'bg-yellow-500/20 text-yellow-400' };
    default: return { label: status || '--', className: 'bg-gray-500/20 text-gray-400' };
  }
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '--';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);
  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function getElapsedSeconds(dateStr: string | null | undefined, nowMs: number): number | null {
  if (!dateStr) return null;
  try {
    const startedAt = parseSqliteDate(dateStr);
    if (isNaN(startedAt.getTime())) return null;
    return Math.max(0, Math.floor((nowMs - startedAt.getTime()) / 1000));
  } catch {
    return null;
  }
}

function getRunningRun(runs: RunEntry[], currentRunAgentSessionId: string | null): RunEntry | null {
  return runs.find((run) => run.id === currentRunAgentSessionId)
    || runs.find((run) => run.status === 'running')
    || null;
}

function getAverageDurationSeconds(runs: RunEntry[]): number | null {
  const durations = runs
    .map((run) => run.durationSeconds)
    .filter((value): value is number => typeof value === 'number' && value > 0);
  if (durations.length === 0) return null;
  return durations.reduce((sum, value) => sum + value, 0) / durations.length;
}

// --- Component ---

const ScheduleDetail: React.FC<ScheduleDetailProps> = ({
  schedule,
  runs: rawRuns,
  token,
  onClose,
  onRefresh,
  onRunNow,
  onStop,
  onToggleEnabled,
}) => {
  const runs = Array.isArray(rawRuns) ? rawRuns : [];
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logFilter, setLogFilter] = useState<LogType | ''>('');
  const [pendingEvents, setPendingEvents] = useState<PendingEvent[]>([]);
  const [loadingPendingEvents, setLoadingPendingEvents] = useState(false);
  const [pendingEventsError, setPendingEventsError] = useState<string | null>(null);
  const [cancellingPendingEventId, setCancellingPendingEventId] = useState<string | 'all' | null>(null);
  const [expandedPendingEventId, setExpandedPendingEventId] = useState<string | null>(null);

  // Prompt state
  const [promptContent, setPromptContent] = useState('');
  const [editedPrompt, setEditedPrompt] = useState('');
  const [promptVersions, setPromptVersions] = useState<PromptVersion[]>([]);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [promptError, setPromptError] = useState<string | null>(null);

  const isRunning = !!schedule.currentRunAgentSessionId;
  const [stopping, setStopping] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const shouldTickClock = isRunning || activeTab === 'events';

  // Reset stopping state when schedule is no longer running
  useEffect(() => {
    if (!isRunning) setStopping(false);
  }, [isRunning]);

  useEffect(() => {
    if (!shouldTickClock) return undefined;
    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [shouldTickClock]);

  const handleStop = async () => {
    setStopping(true);
    try {
      onStop(schedule.id);
    } catch {
      setStopping(false);
    }
  };

  // Inline editing state
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingField, setSavingField] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState(false);

  const startEditing = (field: string, currentValue: string) => {
    setEditingField(field);
    setEditValue(currentValue);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  const saveField = async (field: string) => {
    setSavingField(field);
    try {
      const update: UpdateScheduleData = {};
      if (field === 'model') update.model = editValue || undefined;
      else if (field === 'maxTurns') update.maxTurns = parseInt(editValue, 10) || 50;
      else if (field === 'maxRuntimeSeconds') update.maxRuntimeSeconds = parseInt(editValue, 10) || 600;
      else if (field === 'maxCostUsd') update.maxCostUsd = editValue ? parseFloat(editValue) : null;
      await AgentScheduleService.update(schedule.id, update, token);
      setEditingField(null);
      setEditValue('');
      onRefresh();
    } catch (err: any) {
      console.error('Failed to save field:', err);
    } finally {
      setSavingField(null);
    }
  };

  const handleFieldKeyDown = (e: React.KeyboardEvent, field: string) => {
    if (e.key === 'Enter') saveField(field);
    else if (e.key === 'Escape') cancelEditing();
  };

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(schedule.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch { /* ignore */ }
  };

  // Load logs
  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    setLogsError(null);
    try {
      const data = await AgentScheduleService.getLogs(
        schedule.id,
        token,
        50,
        logFilter ? logFilter as LogType : undefined
      );
      setLogs(data);
    } catch (err: any) {
      console.error('Failed to load logs:', err);
      setLogsError(err?.message || 'Failed to load logs');
    } finally {
      setLoadingLogs(false);
    }
  }, [schedule.id, token, logFilter]);

  const loadPendingEvents = useCallback(async () => {
    setLoadingPendingEvents(true);
    setPendingEventsError(null);
    try {
      const data = await AgentScheduleService.getPendingEvents(schedule.id, token);
      setPendingEvents(data);
    } catch (err: any) {
      console.error('Failed to load pending events:', err);
      setPendingEventsError(err?.message || 'Failed to load pending events');
    } finally {
      setLoadingPendingEvents(false);
    }
  }, [schedule.id, token]);

  // Load prompt
  const loadPrompt = useCallback(async () => {
    setLoadingPrompt(true);
    setPromptError(null);
    try {
      const [prompt, versions] = await Promise.all([
        AgentScheduleService.getPrompt(schedule.id, token),
        AgentScheduleService.getPromptVersions(schedule.id, token),
      ]);
      setPromptContent(prompt.content);
      setEditedPrompt(prompt.content);
      setPromptVersions(versions);
    } catch (err: any) {
      setPromptError(err.message || 'Failed to load prompt');
    } finally {
      setLoadingPrompt(false);
    }
  }, [schedule.id, token]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'logs') loadLogs();
    if (activeTab === 'prompt') loadPrompt();
    if (activeTab === 'events') loadPendingEvents();
  }, [activeTab, loadLogs, loadPendingEvents, loadPrompt]);

  useEffect(() => {
    loadPendingEvents();
  }, [loadPendingEvents]);

  useEffect(() => {
    if (expandedPendingEventId && !pendingEvents.some((event) => event.id === expandedPendingEventId)) {
      setExpandedPendingEventId(null);
    }
  }, [expandedPendingEventId, pendingEvents]);

  // Auto-refresh when running
  useEffect(() => {
    if (isRunning) {
      pollRef.current = setInterval(() => {
        onRefresh();
        if (activeTab === 'logs') loadLogs();
        if (activeTab === 'events') loadPendingEvents();
      }, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isRunning, onRefresh, activeTab, loadLogs, loadPendingEvents]);

  // Save prompt
  const handleSavePrompt = async () => {
    setSavingPrompt(true);
    setPromptError(null);
    try {
      await AgentScheduleService.updatePrompt(schedule.id, editedPrompt, changeSummary || undefined, token);
      setIsEditingPrompt(false);
      setChangeSummary('');
      await loadPrompt();
    } catch (err: any) {
      setPromptError(err.message || 'Failed to save prompt');
    } finally {
      setSavingPrompt(false);
    }
  };

  // Activate prompt version
  const handleActivateVersion = async (versionId: string) => {
    try {
      await AgentScheduleService.activatePromptVersion(schedule.id, versionId, token);
      await loadPrompt();
    } catch (err: any) {
      setPromptError(err.message || 'Failed to activate version');
    }
  };

  const handleCancelPendingEvent = async (eventId: string) => {
    setCancellingPendingEventId(eventId);
    setPendingEventsError(null);
    try {
      await AgentScheduleService.cancelPendingEvent(schedule.id, eventId, token);
      await Promise.all([
        loadPendingEvents(),
        Promise.resolve(onRefresh()),
      ]);
    } catch (err: any) {
      setPendingEventsError(err?.message || 'Failed to cancel pending event');
    } finally {
      setCancellingPendingEventId(null);
    }
  };

  const handleCancelAllPendingEvents = async () => {
    if (pendingEvents.length === 0) return;
    if (!window.confirm(`Cancel ${pendingEvents.length} pending event${pendingEvents.length === 1 ? '' : 's'} for "${schedule.name}"?`)) return;

    setCancellingPendingEventId('all');
    setPendingEventsError(null);
    try {
      await AgentScheduleService.cancelPendingEvents(schedule.id, token);
      setExpandedPendingEventId(null);
      await Promise.all([
        loadPendingEvents(),
        Promise.resolve(onRefresh()),
      ]);
    } catch (err: any) {
      setPendingEventsError(err?.message || 'Failed to cancel pending events');
    } finally {
      setCancellingPendingEventId(null);
    }
  };

  const tabs: { id: DetailTab; label: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'prompt', label: 'Prompt' },
    { id: 'logs', label: 'Logs' },
    {
      id: 'events',
      label: (
        <span className="inline-flex items-center gap-1.5">
          <span>Pending Events</span>
          {pendingEvents.length > 0 && (
            <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-500 dark:text-amber-400">
              {pendingEvents.length}
            </span>
          )}
        </span>
      ),
    },
    { id: 'runs', label: 'Runs' },
  ];
  const runningRun = getRunningRun(runs, schedule.currentRunAgentSessionId);
  const liveDurationSeconds = getElapsedSeconds(runningRun?.createdAt ?? null, nowMs);
  const averageDurationSeconds = getAverageDurationSeconds(runs);
  const pendingEventCountLabel = loadingPendingEvents ? 'Loading...' : pendingEvents.length === 0 ? 'None' : `${pendingEvents.length} queued`;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full sm:max-w-2xl bg-white/95 dark:bg-[#1a2332]/95 backdrop-blur-2xl border-l border-white/10 shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-200/50 dark:border-white/[0.06]">
          <div className="min-w-0 flex-1 mr-3">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                isRunning ? 'bg-[#FFB52B] schedule-pulse-ring' : schedule.enabled ? 'bg-[#FFB52B]' : 'bg-gray-400 dark:bg-gray-600'
              }`} />
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">{schedule.name}</h2>
            </div>
            {schedule.description && (
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">{schedule.description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close detail panel"
            className="p-2.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors flex-shrink-0 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto border-b border-gray-200/50 dark:border-white/[0.06] px-4 sm:px-5 scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`
                px-3 sm:px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap flex-shrink-0 min-h-[44px] sm:min-h-0
                ${activeTab === tab.id
                  ? 'text-[#FFB52B] border-[#FFB52B]'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className={`flex-1 overflow-y-auto ${activeTab === 'prompt' ? 'flex flex-col' : ''}`}>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="p-4 sm:p-5 space-y-5">
              {/* Action buttons */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {isRunning ? (
                  <button
                    type="button"
                    onClick={handleStop}
                    disabled={stopping}
                    aria-label={stopping ? 'Stopping agent' : 'Stop running agent'}
                    className="flex items-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl text-sm font-medium
                      bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px] sm:min-h-0"
                  >
                    {stopping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
                    {stopping ? 'Stopping...' : 'Stop Running Agent'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onRunNow(schedule.id)}
                    disabled={!schedule.enabled}
                    aria-label="Run schedule now"
                    className="flex items-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl text-sm font-medium
                      bg-[#FFB52B]/15 text-[#FFB52B] hover:bg-[#FFB52B]/25 border border-[#FFB52B]/20
                      transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] sm:min-h-0"
                  >
                    <Play className="h-4 w-4" /> Run Now
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onToggleEnabled(schedule.id, schedule.enabled)}
                  aria-label={schedule.enabled ? 'Disable schedule' : 'Enable schedule'}
                  className={`flex items-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl text-sm font-medium border transition-colors min-h-[44px] sm:min-h-0 ${
                    schedule.enabled
                      ? 'bg-gray-500/15 text-gray-600 dark:text-gray-300 hover:bg-gray-500/25 border-gray-500/20'
                      : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/25 border-emerald-500/20'
                  }`}
                >
                  {schedule.enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  {schedule.enabled ? 'Disable' : 'Enable'}
                </button>
              </div>

              {/* Schedule ID */}
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200/60 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.03] backdrop-blur-sm">
                <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider">ID</span>
                <code className="text-xs text-gray-600 dark:text-gray-400 font-mono truncate flex-1">{schedule.id}</code>
                <button
                  type="button"
                  onClick={handleCopyId}
                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                  title="Copy schedule ID"
                  aria-label="Copy schedule ID to clipboard"
                >
                  {copiedId
                    ? <Check className="h-3.5 w-3.5 text-emerald-500" />
                    : <Copy className="h-3.5 w-3.5 text-gray-400" />
                  }
                </button>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-gray-900 dark:text-white">
                <InfoItem label="Cron" value={cronToHuman(schedule.cronExpression)} icon={<Clock className="h-4 w-4" />} />
                <EditableInfoItem
                  label="Model"
                  value={schedule.model || 'Default (Opus)'}
                  field="model"
                  editingField={editingField}
                  editValue={editValue}
                  savingField={savingField}
                  onStartEditing={startEditing}
                  onEditValueChange={setEditValue}
                  onSave={saveField}
                  onCancel={cancelEditing}
                  onKeyDown={handleFieldKeyDown}
                  inputType="select"
                  selectOptions={[
                    { value: '', label: 'Default (Opus)' },
                    { value: 'opus', label: 'Opus' },
                    { value: 'sonnet', label: 'Sonnet' },
                    { value: 'haiku', label: 'Haiku' },
                  ]}
                  rawValue={schedule.model || ''}
                />
                <SliderInfoItem
                  label="Max Turns"
                  field="maxTurns"
                  min={50}
                  formatDisplay={(v: number) => v >= 400 ? "Unlimited" : String(v)}
                  max={400}
                  step={10}
                  value={Math.min(schedule.maxTurns ?? 200, 400)}
                  displayValue={(schedule.maxTurns ?? 200) >= 400 ? 'Unlimited' : String(schedule.maxTurns ?? 200)}
                  editingField={editingField}
                  editValue={editValue}
                  savingField={savingField}
                  onStartEditing={(f, v) => startEditing(f, String(Math.min(Number(v) || 200, 400)))}
                  onEditValueChange={setEditValue}
                  onSave={(field) => {
                    const v = Number(editValue);
                    if (v >= 400) { setEditValue('999999'); }
                    saveField(field);
                  }}
                  onCancel={cancelEditing}
                  ticks={['50', '100', '200', '300', '∞']}
                />
                <SliderInfoItem
                  label="Max Runtime"
                  value={formatDuration(schedule.maxRuntimeSeconds ?? 600)}
                  field="maxRuntimeSeconds"
                  editingField={editingField}
                  editValue={editValue}
                  savingField={savingField}
                  onStartEditing={startEditing}
                  onEditValueChange={setEditValue}
                  onSave={saveField}
                  onCancel={cancelEditing}
                  rawValue={String(schedule.maxRuntimeSeconds ?? 600)}
                />
                <EditableInfoItem
                  label="Cost Limit"
                  value={schedule.maxCostUsd ? `$${schedule.maxCostUsd}` : 'None'}
                  field="maxCostUsd"
                  editingField={editingField}
                  editValue={editValue}
                  savingField={savingField}
                  onStartEditing={startEditing}
                  onEditValueChange={setEditValue}
                  onSave={saveField}
                  onCancel={cancelEditing}
                  onKeyDown={handleFieldKeyDown}
                  inputType="number"
                  rawValue={schedule.maxCostUsd ? String(schedule.maxCostUsd) : ''}
                />
                <InfoItem label="Total Cost" value={`$${(schedule.totalCostUsd ?? 0).toFixed(2)}`} />
                <InfoItem label="Total Runs" value={String(schedule.runCount ?? 0)} />
                <InfoItem label="Avg Runtime" value={formatDuration(averageDurationSeconds)} />
                <InfoItem
                  label="Current Run"
                  value={liveDurationSeconds != null
                    ? (
                      <span title={exactDateTime(runningRun?.createdAt) || undefined}>
                        {formatDuration(liveDurationSeconds)}
                      </span>
                    )
                    : 'Idle'}
                />
                <InfoItem label="Consecutive Failures" value={String(schedule.consecutiveFailures ?? 0)} />
                <InfoItem label="Last Run" value={<TimestampSpan dateStr={schedule.lastRunAt} />} />
                <InfoItem
                  label="Next Run"
                  value={schedule.enabled
                    ? (
                      <span title={exactDateTime(schedule.nextRunAt) || undefined}>
                        {formatNextRunTime(schedule.nextRunAt) || <TimestampSpan dateStr={schedule.nextRunAt} />}
                      </span>
                    )
                    : 'Disabled'
                  }
                />
                <InfoItem
                  label="Pending Events"
                  value={pendingEvents.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setActiveTab('events')}
                      className="text-left text-amber-600 transition-colors hover:text-amber-500 dark:text-amber-400 dark:hover:text-amber-300"
                    >
                      {pendingEventCountLabel}
                    </button>
                  ) : pendingEventCountLabel}
                />
                <InfoItem label="Created" value={<TimestampSpan dateStr={schedule.createdAt} />} />
                <InfoItem label="Created By" value={schedule.createdBy || '--'} />
              </div>

              {/* Triggers section */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Triggers</h3>
                <div className="space-y-2">
                  {!schedule.cronExpression && !(Array.isArray(schedule.eventTriggers) && schedule.eventTriggers.length > 0) && !schedule.dependsOnScheduleId && (
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic">No triggers configured</p>
                  )}
                  {schedule.cronExpression && (
                    <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <Clock className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                        <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded px-2 py-0.5">{schedule.cronExpression}</span>
                        <span className="text-gray-500 text-xs sm:text-sm">{cronToHuman(schedule.cronExpression)}</span>
                      </div>
                    </div>
                  )}
                  {Array.isArray(schedule.eventTriggers) && schedule.eventTriggers.length > 0 && (
                    <div className="flex items-start gap-2 text-sm">
                      <Zap className="h-4 w-4 text-amber-500 mt-0.5" />
                      <div className="flex flex-wrap gap-1.5">
                        {(schedule.eventTriggers || []).map((evt) => (
                          <span key={evt} className="px-2 py-0.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-xs font-medium">
                            {evt}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {schedule.dependsOnScheduleId && (
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <GitBranch className="h-4 w-4 text-purple-400" />
                      <span>Depends on: <span className="font-mono text-xs">{schedule.dependsOnScheduleId}</span></span>
                    </div>
                  )}
                  {(schedule.eventDelaySeconds ?? 0) > 0 && (
                    <div className="text-xs text-gray-500">
                      Event delay: {schedule.eventDelaySeconds}s debounce
                    </div>
                  )}
                  {pendingEvents.length > 0 && (
                    <div className="text-xs text-amber-600 dark:text-amber-400">
                      {pendingEvents.length} pending event{pendingEvents.length === 1 ? '' : 's'} queued
                    </div>
                  )}
                </div>
              </div>

              {/* Run history orbs */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Recent Runs</h3>
                <RunHistoryOrbs runs={runs} maxVisible={12} />
              </div>
            </div>
          )}

          {/* Prompt Tab */}
          {activeTab === 'prompt' && (
            <div className="flex-1 min-h-0 flex flex-col p-4 sm:p-5">
              {loadingPrompt ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  {promptError && (
                    <div className="p-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm flex-shrink-0 mb-3">
                      {promptError}
                    </div>
                  )}

                  {/* Prompt header */}
                  <div className="flex items-center justify-between mb-2 flex-shrink-0">
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Active Prompt
                    </h3>
                    {!isEditingPrompt && (
                      <button
                        type="button"
                        onClick={() => setIsEditingPrompt(true)}
                        className="text-xs text-[#FFB52B] hover:text-[#FFB52B]/80 transition-colors"
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  {/* Prompt textarea - fills remaining space */}
                  <textarea
                    value={isEditingPrompt ? editedPrompt : promptContent}
                    onChange={(e) => setEditedPrompt(e.target.value)}
                    readOnly={!isEditingPrompt}
                    className={`
                      w-full flex-1 min-h-0 rounded-xl border p-3 text-sm font-mono resize-none
                      bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500
                      ${isEditingPrompt
                        ? 'border-[#FFB52B]/30 focus:border-[#FFB52B]/50 focus:ring-1 focus:ring-[#FFB52B]/20'
                        : 'border-gray-200 dark:border-white/10 cursor-default'
                      }
                      focus:outline-none
                    `}
                  />

                  {/* Change summary + save buttons - pinned below textarea */}
                  {isEditingPrompt && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mt-3 flex-shrink-0">
                      <input
                        type="text"
                        value={changeSummary}
                        onChange={(e) => setChangeSummary(e.target.value)}
                        placeholder="Change summary (optional)"
                        className="flex-1 px-3 py-2 sm:py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-gray-900/50 text-sm text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-[#FFB52B]/50 dark:focus:border-[#FFB52B]/30"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleSavePrompt}
                          disabled={savingPrompt || editedPrompt === promptContent}
                          className="flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-medium
                            bg-[#FFB52B]/15 text-[#FFB52B] hover:bg-[#FFB52B]/25 border border-[#FFB52B]/20
                            transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
                        >
                          {savingPrompt ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => { setIsEditingPrompt(false); setEditedPrompt(promptContent); setChangeSummary(''); }}
                          className="px-3 py-2 sm:py-1.5 rounded-lg text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors min-h-[44px] sm:min-h-0"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Version history - pinned at bottom */}
                  {promptVersions.length > 0 && (
                    <div className="mt-4 flex-shrink-0">
                      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        Version History
                      </h3>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {promptVersions.map((v) => (
                          <div
                            key={v.id}
                            className={`
                              flex items-center justify-between p-2.5 rounded-lg border transition-colors
                              ${v.isActive
                                ? 'border-[#FFB52B]/30 bg-[#FFB52B]/5'
                                : 'border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-gray-900/30 hover:bg-gray-100 dark:hover:bg-gray-900/50'
                              }
                            `}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">v{v.versionNumber}</span>
                                {v.isActive && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FFB52B]/20 text-[#FFB52B]">active</span>
                                )}
                              </div>
                              <div className="text-[11px] text-gray-500 dark:text-gray-500 truncate">
                                {v.changeSummary || 'No change summary'}
                              </div>
                              <div className="text-[10px] text-gray-400 dark:text-gray-600">
                                <TimestampSpan dateStr={v.createdAt} /> by {v.createdBy}
                              </div>
                            </div>
                            {!v.isActive && (
                              <button
                                type="button"
                                onClick={() => handleActivateVersion(v.id)}
                                className="ml-3 flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium
                                  text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                              >
                                <RotateCcw className="h-3 w-3" /> Revert
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <div className="p-4 sm:p-5 space-y-3">
              {/* Filter row */}
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={logFilter}
                  onChange={(e) => setLogFilter(e.target.value as LogType | '')}
                  aria-label="Filter log entries by type"
                  className="pl-3 pr-8 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:border-[#FFB52B]/50 dark:focus:border-[#FFB52B]/30 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m2%204%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat"
                >
                  <option value="">All types</option>
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="error">Error</option>
                  <option value="context">Context</option>
                  <option value="run_start">Run Start</option>
                  <option value="run_end">Run End</option>
                </select>
                <button
                  type="button"
                  onClick={loadLogs}
                  disabled={loadingLogs}
                  aria-label="Refresh logs"
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                >
                  <RotateCcw className={`h-3.5 w-3.5 text-gray-400 ${loadingLogs ? 'animate-spin' : ''}`} />
                </button>
                {isRunning && (
                  <span className="text-[10px] text-blue-400 flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                    Auto-refreshing
                  </span>
                )}
              </div>

              {/* Logs error */}
              {logsError && (
                <div className="p-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm">
                  {logsError}
                </div>
              )}

              {/* Log entries */}
              {loadingLogs && logs.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No log entries</div>
              ) : (
                <div className="space-y-1">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-start gap-2 p-2.5 rounded-lg border border-gray-200/60 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.03] hover:bg-gray-100 dark:hover:bg-white/[0.05] backdrop-blur-sm transition-colors"
                    >
                      {logTypeIcon(log.logType)}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${logTypeBadgeClass(log.logType)}`}>
                            {log.logType}
                          </span>
                          <span className="text-[10px] text-gray-400 dark:text-gray-600">
                            <TimestampSpan dateStr={log.createdAt} />
                          </span>
                        </div>
                        <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                          {log.message}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Pending Events Tab */}
          {activeTab === 'events' && (
            <div className="p-4 sm:p-5 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Queued Event Triggers
                  </h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    Inspect delayed event payloads and cancel them before they fire.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={loadPendingEvents}
                    disabled={loadingPendingEvents}
                    aria-label="Refresh pending events"
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                  >
                    <RotateCcw className={`h-3.5 w-3.5 text-gray-400 ${loadingPendingEvents ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelAllPendingEvents}
                    disabled={pendingEvents.length === 0 || cancellingPendingEventId === 'all'}
                    className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-500 transition-colors hover:bg-red-500/15 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {cancellingPendingEventId === 'all'
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <XCircle className="h-3.5 w-3.5" />
                    }
                    Cancel All
                  </button>
                </div>
              </div>

              {pendingEventsError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400">
                  {pendingEventsError}
                </div>
              )}

              {loadingPendingEvents && pendingEvents.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : pendingEvents.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200/80 bg-gray-50/80 p-8 text-center text-sm text-gray-500 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-gray-400">
                  No pending events.
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingEvents.map((event) => {
                    const payloadText = stringifyEventPayload(event.eventPayloadJson);
                    const isExpanded = expandedPendingEventId === event.id;
                    const fireDate = parseSqliteDate(event.fireAt);
                    const countdown = isNaN(fireDate.getTime()) ? '--' : timeUntilFromDate(fireDate);

                    return (
                      <div
                        key={event.id}
                        className="rounded-xl border border-gray-200/60 bg-gray-50/80 p-3 backdrop-blur-sm dark:border-white/[0.06] dark:bg-white/[0.03]"
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                                <Zap className="h-3 w-3" />
                                {event.eventName}
                              </span>
                              <span className="text-[11px] text-gray-500 dark:text-gray-400" title={exactDateTime(event.createdAt) || undefined}>
                                queued <TimestampSpan dateStr={event.createdAt} />
                              </span>
                            </div>

                            <div className="mt-2 grid grid-cols-1 gap-2 text-xs text-gray-600 dark:text-gray-300 sm:grid-cols-2">
                              <div className="rounded-lg border border-gray-200/60 bg-white/70 px-3 py-2 dark:border-white/[0.06] dark:bg-black/10">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-500">Fires At</div>
                                <div className="mt-1 font-medium text-gray-800 dark:text-gray-100">
                                  {formatNextRunTime(event.fireAt) || exactDateTime(event.fireAt) || '--'}
                                </div>
                                <div className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">{countdown}</div>
                              </div>

                              <div className="rounded-lg border border-gray-200/60 bg-white/70 px-3 py-2 dark:border-white/[0.06] dark:bg-black/10">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-500">Source</div>
                                <div className="mt-1 break-all font-mono text-[11px] text-gray-700 dark:text-gray-300">
                                  {event.sessionId || 'No source session'}
                                </div>
                              </div>
                            </div>

                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                              Payload: {pendingEventPreview(event.eventPayloadJson)}
                            </div>

                            {isExpanded && (
                              <pre className="mt-3 max-h-64 overflow-auto rounded-lg border border-gray-200/60 bg-gray-950 p-3 text-[11px] leading-5 text-gray-100 dark:border-white/[0.08]">
                                {payloadText || 'No payload'}
                              </pre>
                            )}
                          </div>

                          <div className="flex items-center gap-2 sm:ml-3 sm:flex-col sm:items-stretch">
                            <button
                              type="button"
                              onClick={() => setExpandedPendingEventId(isExpanded ? null : event.id)}
                              className="rounded-lg border border-gray-200/80 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:border-white/[0.08] dark:text-gray-300 dark:hover:bg-white/10"
                            >
                              {isExpanded ? 'Hide Payload' : 'Inspect Payload'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCancelPendingEvent(event.id)}
                              disabled={cancellingPendingEventId === event.id}
                              className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-500 transition-colors hover:bg-red-500/15 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {cancellingPendingEventId === event.id ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  Cancelling...
                                </span>
                              ) : 'Cancel Event'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Runs Tab */}
          {activeTab === 'runs' && (
            <div className="p-4 sm:p-5">
              {runs.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No runs yet</div>
              ) : (
                <div className="space-y-1.5">
                  {runs.map((run) => {
                    const badge = statusBadge(run.status);
                    const runDurationSeconds = run.durationSeconds ?? (
                      run.status === 'running'
                        ? getElapsedSeconds(run.createdAt, nowMs)
                        : null
                    );
                    return (
                      <div
                        key={run.id}
                        className="flex items-start sm:items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border border-gray-200/60 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.03] hover:bg-gray-100 dark:hover:bg-white/[0.05] backdrop-blur-sm transition-colors"
                      >
                        {/* Status orb */}
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                          run.status === 'success' ? 'bg-[#FFB52B]' :
                          run.status === 'failure' || run.status === 'timeout' ? 'bg-red-500' :
                          run.status === 'running' ? 'bg-[#FFB52B] schedule-pulse-ring' :
                          'bg-gray-400'
                        }`} />

                        {/* Info */}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-0.5">
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${badge.className}`}>
                              {badge.label}
                            </span>
                            <span className="text-[10px] text-gray-500 capitalize">
                              {run.triggerType || 'unknown'}
                            </span>
                            {run.triggerEvent && (
                              <span className="text-[10px] text-amber-500 truncate max-w-[120px] sm:max-w-none">
                                {run.triggerEvent}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-500 flex flex-wrap gap-x-2">
                            <TimestampSpan dateStr={run.createdAt} />
                            {runDurationSeconds != null && (
                              <span className={run.status === 'running' ? 'text-[#B57700] dark:text-[#FFCF74]' : ''}>
                                {formatDuration(runDurationSeconds)}
                                {run.status === 'running' ? ' live' : ''}
                              </span>
                            )}
                            {run.totalTokens != null && run.totalTokens > 0 && (
                              <span>{Number(run.totalTokens).toLocaleString()} tokens</span>
                            )}
                            {run.totalCost != null && run.totalCost > 0 && (
                              <span>${Number(run.totalCost).toFixed(3)}</span>
                            )}
                          </div>
                          {run.errorMessage && (
                            <p className="text-[11px] text-red-400 mt-0.5 truncate">{run.errorMessage}</p>
                          )}
                        </div>

                        {/* Link to agent session */}
                        {run.id && run.status !== 'pending' && (
                          <button
                            type="button"
                            onClick={() => {
                              const params = new URLSearchParams({
                                schedule: schedule.id,
                                run: run.id,
                              });
                              window.open(`/agents?${params.toString()}`, '_blank');
                            }}
                            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                            title="View agent session"
                            aria-label="View agent session in new tab"
                          >
                            <ExternalLink className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Small helper component for the info grid
const InfoItem: React.FC<{ label: string; value: React.ReactNode; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="p-3 rounded-xl border border-gray-200/60 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.03] backdrop-blur-sm">
    <div className="flex items-center gap-1.5 mb-1">
      {icon && <span className="text-gray-400 dark:text-gray-500">{icon}</span>}
      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider">{label}</span>
    </div>
    <div className="text-sm text-gray-700 dark:text-gray-300 truncate">{value}</div>
  </div>
);

// Editable info item for inline field editing
const EditableInfoItem: React.FC<{
  label: string;
  value: React.ReactNode;
  field: string;
  editingField: string | null;
  editValue: string;
  savingField: string | null;
  onStartEditing: (field: string, currentValue: string) => void;
  onEditValueChange: (value: string) => void;
  onSave: (field: string) => void;
  onCancel: () => void;
  onKeyDown: (e: React.KeyboardEvent, field: string) => void;
  inputType?: 'text' | 'number' | 'select';
  selectOptions?: { value: string; label: string }[];
  rawValue: string;
}> = ({ label, value, field, editingField, editValue, savingField, onStartEditing, onEditValueChange, onSave, onCancel, onKeyDown, inputType = 'text', selectOptions, rawValue }) => {
  const isEditing = editingField === field;
  const isSaving = savingField === field;

  if (isEditing) {
    return (
      <div className="p-3 rounded-xl border border-[#FFB52B]/30 bg-[#FFB52B]/5">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider">{label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {inputType === 'select' && selectOptions ? (
            <select
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={(e) => onKeyDown(e, field)}
              autoFocus
              className="flex-1 px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900/50 text-sm text-gray-900 dark:text-gray-200 focus:outline-none focus:border-[#FFB52B]/50"
            >
              {selectOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <input
              type={inputType}
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={(e) => onKeyDown(e, field)}
              autoFocus
              className="flex-1 px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900/50 text-sm text-gray-900 dark:text-gray-200 focus:outline-none focus:border-[#FFB52B]/50 w-full min-w-0"
            />
          )}
          <button
            type="button"
            onClick={() => onSave(field)}
            disabled={isSaving}
            className="p-1 rounded hover:bg-[#FFB52B]/20 transition-colors"
            title="Save"
          >
            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[#FFB52B]" /> : <Check className="h-3.5 w-3.5 text-[#FFB52B]" />}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
            title="Cancel"
          >
            <X className="h-3.5 w-3.5 text-gray-400" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 rounded-xl border border-gray-200/60 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.03] backdrop-blur-sm group/edit">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider">{label}</span>
        <button
          type="button"
          onClick={() => onStartEditing(field, rawValue || String(value) || "")}
          className="opacity-0 group-hover/edit:opacity-100 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-white/10 transition-all"
          title={`Edit ${label}`}
        >
          <Pencil className="h-2.5 w-2.5 text-gray-400" />
        </button>
      </div>
      <div className="text-sm text-gray-700 dark:text-gray-300 truncate">{value}</div>
    </div>
  );
};

// Generic slider info item
const SliderInfoItem: React.FC<{
  label: string;
  value?: React.ReactNode;
  displayValue?: string;
  field: string;
  editingField: string | null;
  editValue: string;
  savingField: string | null;
  onStartEditing: (field: string, currentValue: string) => void;
  onEditValueChange: (value: string) => void;
  onSave: (field: string) => void;
  onCancel: () => void;
  rawValue?: string;
  min?: number;
  max?: number;
  step?: number;
  ticks?: string[];
  formatDisplay?: (val: number) => string;
}> = ({ label, value, displayValue, field, editingField, editValue, savingField, onStartEditing, onEditValueChange, onSave, onCancel, rawValue, min = 60, max = 7200, step = 60, ticks, formatDisplay }) => {
  const isEditing = editingField === field;
  const isSaving = savingField === field;
  const displayFn = formatDisplay || formatDuration;

  if (isEditing) {
    const numVal = parseInt(editValue, 10) || min;
    const isMaxInfinite = ticks && ticks[ticks.length - 1] === '∞' && numVal >= max;
    return (
      <div className="p-3 rounded-xl border border-[#FFB52B]/30 bg-[#FFB52B]/5">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
            <span className="text-sm font-mono text-gray-700 dark:text-gray-300">{isMaxInfinite ? 'Unlimited' : displayFn(numVal)}</span>
          </div>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={numVal}
            onChange={(e) => onEditValueChange(e.target.value)}
            className="w-full accent-[#FFB52B]"
          />
          <div className="flex justify-between text-[9px] text-gray-400">
            {(ticks || ['1m', '15m', '30m', '1h', '2h']).map((t, i) => <span key={i}>{t}</span>)}
          </div>
        </div>
        <div className="flex items-center justify-end gap-1.5 mt-2">
          <button
            type="button"
            onClick={() => onSave(field)}
            disabled={isSaving}
            className="p-1 rounded hover:bg-[#FFB52B]/20 transition-colors"
            title="Save"
          >
            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[#FFB52B]" /> : <Check className="h-3.5 w-3.5 text-[#FFB52B]" />}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
            title="Cancel"
          >
            <X className="h-3.5 w-3.5 text-gray-400" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 rounded-xl border border-gray-200/60 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.03] backdrop-blur-sm group/edit">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider">{label}</span>
        <button
          type="button"
          onClick={() => onStartEditing(field, rawValue || String(value) || "")}
          className="opacity-0 group-hover/edit:opacity-100 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-white/10 transition-all"
          title={`Edit ${label}`}
        >
          <Pencil className="h-2.5 w-2.5 text-gray-400" />
        </button>
      </div>
      <div className="text-sm text-gray-700 dark:text-gray-300 truncate">{value}</div>
    </div>
  );
};

export default ScheduleDetail;
